import React, { useState, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { dtcListState, vehicleState } from './states';

import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';


import { DtcList } from './DtcList';
import { StyledButton } from './components/shared/StyledButton';
import plusIcon from './assets/plus-icon.png';
import './VehicleSelection.css';
import { getYears, getMakes, getModels } from './services/VehicleService';
import { LocalStorageService } from './services/LocalStorageService';


interface Year {
    yearId: number
    yearValue: number
}
interface Make {
    makeId: number
    makeName: string
}
interface Model {
    modelId: number
    modelName: string
}

const VehicleSelection = () => {
    const [vehicle, setVehicle] = useRecoilState(vehicleState);
    const [dtcs, setDtcs] = useRecoilState(dtcListState);

    const [years, setYears] = useState<Year[]>([]);
    const [getYearsLoading, setGetYearsLoading] = useState<boolean>(false);
    const [makes, setMakes] = useState<Make[]>([]);
    const [getMakesLoading, setGetmakesLoading] = useState<boolean>(false);
    const [models, setModels] = useState<Model[]>([]);
    const [getModelsLoading, setGetModelsLoading] = useState<boolean>(false);
    const [selectMake, setSelectMake] = useState<number>();
    const [selectModel, setSelectModel] = useState<number>();
    const [selectYear, setSelectYear] = useState<number>();

    useEffect(() => {
        setSelectMake(0);
        setSelectModel(0);
        setMakes([]);
        setModels([]);
        const makesKey = "makeApiData" + selectYear;
        const makesData = LocalStorageService.getItem<[]>(makesKey);
        if (makesData) {
            setMakes(makesData)
        } else {
            if (!getMakesLoading && selectYear) {
                setGetmakesLoading(true);
                getMakes(selectYear)
                    .then(resp => {
                        setMakes(resp.data.makes)
                        LocalStorageService.setItem<[]>(makesKey, resp.data.makes);
                    })
                    .catch(error => console.error('Error fetching data:', error))
                    .finally(() => setGetmakesLoading(false));
            }
        }
        // eslint-disable-next-line
    }, [selectYear]);

    useEffect(() => {
        const yearsKey = "yearApiData";
        const yearsData = LocalStorageService.getItem<[]>(yearsKey);
        if (yearsData) {
            setYears(yearsData)
        }
        else {
            if (!getYearsLoading) {
                setGetYearsLoading(true);
                getYears()
                    .then(response => {
                        setYears(response.data.years)
                        LocalStorageService.setItem<[]>(yearsKey, response.data.years);
                    })
                    .catch(error => console.error('Error fetching data:', error))
                    .finally(() => setGetYearsLoading(false));
            }
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setSelectModel(0);
        setModels([]);
        if (!getModelsLoading && selectMake && selectYear) {
            setGetModelsLoading(true);

            getModels(selectYear, selectMake)
                .then(response => setModels(response.data.models))
                .catch(error => console.error('Error fetching data:', error))
                .finally(() => setGetModelsLoading(false));
        }
        // eslint-disable-next-line
    }, [selectMake]);

    const selectVehicle = (type: string, value: number, label: string) => {
        if (type === "year") {
            setSelectYear(Number(value));
            setSelectMake(0);
            setSelectModel(0);
            let newVehicle = { ...vehicle };
            newVehicle.make = undefined;
            newVehicle.model = undefined;
            newVehicle.year = label;
            setVehicle(newVehicle);
        } else if (type === "make") {
            setSelectMake(Number(value));
            setSelectModel(0);
            let newVehicle = { ...vehicle };
            newVehicle.model = undefined;
            newVehicle.make = label;
            setVehicle(newVehicle);
        } else if (type === "model") {
            setSelectModel(Number(value));
            let newVehicle = { ...vehicle };
            newVehicle.model = label;
            setVehicle(newVehicle);
        }
    }

    return (
        <Container fluid={true} className="form-container">
            <form>
                <Row className="select-row selection-row">
                    <Col sm={4} className="form-group">
                        <label htmlFor="selectForYear" className='float-left'>Select Year<span className='required'>*</span></label>
                        <Autocomplete
                            id="selectForYear"
                            disableClearable
                            options={years?.length ? years?.map(m => ({ id: m.yearId, label: String(m.yearValue) })) : []}
                            defaultValue={{ id: 0, label: 'Select Year' }}
                            sx={{
                                width: 158,
                                '& .MuiOutlinedInput-root': {
                                    padding: '3px',
                                    fontSize: 18
                                }
                            }}
                            renderInput={(params) => <TextField sx={{ backgroundColor: 'white' }} {...params} />}
                            onChange={(event, newValue) => newValue && selectVehicle('year', newValue.id, newValue.label)}
                            loading={getYearsLoading}
                        />
                    </Col>
                    <Col sm={4} className="form-group">
                        <label htmlFor="selectForMake" className='float-left'>Select Make<span className='required'>*</span></label>
                        <Autocomplete
                            id="selectForMake"
                            disableClearable
                            options={makes?.length ? makes?.map(m => ({ id: m.makeId, label: String(m.makeName) })) : []}
                            defaultValue={{ id: 0, label: 'Select Make' }}
                            sx={{
                                width: 158,
                                '& .MuiOutlinedInput-root': {
                                    padding: '3px',
                                    fontSize: 18
                                }
                            }}
                            renderInput={(params) => <TextField sx={{ backgroundColor: 'white' }} {...params} />}
                            onChange={(event, newValue) => newValue && selectVehicle('make', newValue.id, newValue.label)}
                            loading={getMakesLoading}
                        />
                    </Col>
                    <Col sm={4} className="form-group">
                        <label htmlFor="selectForModel" className='float-left'>Select Model<span className='required'>*</span></label>
                        <Autocomplete
                            id="selectForModel"
                            disableClearable
                            options={models?.length ? models?.map(m => ({ id: m.modelId, label: String(m.modelName) })) : []}
                            defaultValue={{ id: 0, label: 'Select Model' }}
                            sx={{
                                width: 158,
                                '& .MuiOutlinedInput-root': {
                                    padding: '3px',
                                    fontSize: 18
                                }
                            }}
                            renderInput={(params) => <TextField sx={{ backgroundColor: 'white' }} {...params} />}
                            onChange={(event, newValue) => newValue && selectVehicle('model', newValue.id, newValue.label)}
                            loading={getModelsLoading}
                        />
                    </Col>
                </Row>
                <Row className="select-row selection-row">
                    <Col sm={12} className="form-group dtc-group">
                        <div className='dtc-button'>
                            <label htmlFor="addDtc" className='dtc-section'>Diagnostic Trouble Code<div className='optional-badge'>optional</div></label>
                            <StyledButton
                                backgroundColor='#D32F2F'
                                onClick={(e) => {
                                    e.preventDefault();
                                    setDtcs([...dtcs, {
                                        id: dtcs.length + 1,
                                        dtc: '',
                                        // severity: ''
                                    }]);
                                }}
                                buttonText={'Add DTC'}
                                image={<img src={plusIcon} alt="Plus Icon" style={{ width: '20px', height: '20px' }} />}
                                name="addDtc" />
                        </div>
                        <DtcList />
                    </Col>
                </Row>
            </form>
        </Container>
    );
};

export default VehicleSelection;
